/**
 * Phase 4 admin tabs:
 *  - ScheduleTab      (a) Calendar / day view of bookings
 *  - QuotationsTab    (b) Quotations + PDF preview
 *  - WorkOrdersTab    (c) Admin view of work orders + photos
 *  - AMCTab           (d) Annual maintenance contracts
 *  - InventoryTab     (e) Stock items + movements
 *  - WhatsAppTab      (f) Templates + ready-to-send queue
 *  - ReviewsTab       (g) Post-service reviews + moderation
 *
 * All tabs use the `api` axios instance (Bearer JWT) injected by AdminPortal.
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Calendar, CalendarDays, FileText, Wrench, RefreshCw, Package, MessageCircle, Star,
  Plus, Edit2, Trash2, Download, Send, Eye, X, AlertCircle, CheckCircle2,
  Phone, Filter, ExternalLink, Loader2, ImageIcon, ArrowDown, ArrowUp, Clock, Bug,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';

const fmtMoney = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const fmtDate = (s) => (s ? String(s).slice(0, 10) : '—');

// ============================================================
// SHARED PRIMITIVES
// ============================================================
function SectionHeader({ title, subtitle, actions }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-slate-900">{title}</h2>
        {subtitle && <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
      <div className="flex gap-2">{actions}</div>
    </div>
  );
}

function EmptyState({ icon: Icon, title, hint }) {
  return (
    <div className="border-2 border-dashed border-slate-200 rounded-2xl p-10 text-center bg-white">
      <Icon size={40} className="mx-auto text-slate-300 mb-3" />
      <div className="font-semibold text-slate-700">{title}</div>
      {hint && <div className="text-sm text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}

function Modal({ open, onClose, title, children, footer, wide }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className={`bg-white rounded-2xl shadow-2xl w-full ${wide ? 'max-w-3xl' : 'max-w-lg'} max-h-[90vh] overflow-hidden flex flex-col`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-bold text-slate-900">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="p-5 overflow-y-auto flex-1">{children}</div>
        {footer && <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">{footer}</div>}
      </div>
    </div>
  );
}

// ============================================================
// (a) SCHEDULE
// ============================================================
export function ScheduleTab({ api }) {
  const [from, setFrom] = useState(() => new Date().toISOString().slice(0, 10));
  const [days, setDays] = useState(90);
  const [data, setData] = useState({ bookings: [], technicians: [], date_counts: {} });
  const [loading, setLoading] = useState(false);
  const [techFilter, setTechFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [allUpcoming, setAllUpcoming] = useState(false);

  const to = useMemo(() => {
    const d = new Date(from);
    d.setDate(d.getDate() + Number(days || 0));
    return d.toISOString().slice(0, 10);
  }, [from, days]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ from_: from });
      if (allUpcoming) qs.set('all_upcoming', 'true');
      else qs.set('to', to);
      if (techFilter !== 'all') qs.set('tech_id', techFilter);
      if (statusFilter !== 'all') qs.set('status', statusFilter);
      const res = await api('GET', `/admin/schedule?${qs.toString()}`);
      setData(res.data || { bookings: [], technicians: [], date_counts: {} });
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  }, [api, from, to, allUpcoming, techFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  // Group bookings by scheduled_date
  const grouped = useMemo(() => {
    const map = {};
    (data.bookings || []).forEach(b => {
      const d = b.scheduled_date || (b.created_at || '').slice(0, 10) || 'unscheduled';
      (map[d] = map[d] || []).push(b);
    });
    return Object.entries(map).sort((a, b) => a[0].localeCompare(b[0]));
  }, [data.bookings]);

  const reassign = async (bookingId, technician_id) => {
    try {
      await api('PATCH', `/admin/bookings/${bookingId}`, { technician_id: technician_id || null });
      await load();
    } catch (e) { alert('Failed to assign: ' + (e.response?.data?.detail || e.message)); }
  };

  const reschedule = async (bookingId, scheduled_date) => {
    try {
      await api('PATCH', `/admin/bookings/${bookingId}`, { scheduled_date });
      await load();
    } catch (e) { alert('Failed to reschedule: ' + (e.response?.data?.detail || e.message)); }
  };

  const updateSlot = async (bookingId, time_slot_start, time_slot_end) => {
    try {
      await api('PATCH', `/admin/bookings/${bookingId}`, { time_slot_start, time_slot_end });
      await load();
    } catch (e) { alert('Failed to update slot: ' + (e.response?.data?.detail || e.message)); }
  };

  return (
    <div data-testid="schedule-tab">
      <SectionHeader
        title="Schedule"
        subtitle="Day-by-day view of bookings · reassign technicians, reschedule, set time slots"
        actions={
          <Button variant="outline" size="sm" onClick={load} data-testid="schedule-refresh">
            <RefreshCw size={14} className="mr-1.5" /> Refresh
          </Button>
        }
      />
      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4 flex flex-wrap gap-3 items-end">
        <div>
          <Label className="text-xs">From</Label>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9" data-testid="schedule-from" disabled={allUpcoming} />
        </div>
        <div>
          <Label className="text-xs">Days</Label>
          <Input type="number" min={1} max={365} value={days} onChange={(e) => setDays(e.target.value)} className="h-9 w-20" data-testid="schedule-days" disabled={allUpcoming} />
        </div>
        <div>
          <Label className="text-xs">Technician</Label>
          <select
            value={techFilter}
            onChange={(e) => setTechFilter(e.target.value)}
            className="h-9 px-3 rounded-md border border-slate-300 bg-white text-sm"
            data-testid="schedule-tech-filter"
          >
            <option value="all">All</option>
            {(data.technicians || []).map(t => (
              <option key={t.id} value={t.id}>{t.name} ({t.employee_code || t.mobile})</option>
            ))}
          </select>
        </div>
        <div>
          <Label className="text-xs">Status</Label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-9 px-3 rounded-md border border-slate-300 bg-white text-sm"
            data-testid="schedule-status-filter"
          >
            <option value="all">All</option>
            <option value="scheduled">Scheduled</option>
            <option value="on_the_way">On the way</option>
            <option value="in_progress">In progress</option>
            <option value="completed">Completed</option>
            <option value="warranty_active">Warranty active</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        <label className="flex items-center gap-2 text-xs font-medium text-slate-700 h-9 px-3 rounded-md border border-slate-300 bg-white cursor-pointer" data-testid="schedule-all-upcoming">
          <input
            type="checkbox"
            checked={allUpcoming}
            onChange={(e) => setAllUpcoming(e.target.checked)}
            className="accent-emerald-600"
          />
          Show all upcoming
        </label>
        <div className="text-xs text-slate-500 ml-auto">
          {data.total ?? (data.bookings || []).length} booking{(data.total ?? 0) === 1 ? '' : 's'} in range
        </div>
      </div>

      {loading && <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>}
      {!loading && grouped.length === 0 && <EmptyState icon={Calendar} title="No bookings in range" hint="Try widening the date range, toggle 'Show all upcoming', or clear filters." />}

      <div className="space-y-5">
        {grouped.map(([day, items]) => (
          <div key={day} className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
              <div className="font-semibold text-slate-900">{day === 'unscheduled' ? 'Unscheduled' : day}</div>
              <div className="text-xs text-slate-500">{items.length} booking{items.length > 1 ? 's' : ''}</div>
            </div>
            <div className="divide-y divide-slate-100">
              {items.map(b => (
                <ScheduleRow
                  key={b.id}
                  booking={b}
                  technicians={data.technicians || []}
                  onReassign={reassign}
                  onReschedule={reschedule}
                  onSlotChange={updateSlot}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const LEAD_SOURCE_COLORS = {
  website: 'bg-blue-100 text-blue-700',
  whatsapp: 'bg-emerald-100 text-emerald-700',
  phone_call: 'bg-indigo-100 text-indigo-700',
  manual: 'bg-slate-100 text-slate-700',
  walk_in: 'bg-amber-100 text-amber-700',
  referral: 'bg-purple-100 text-purple-700',
  social_media: 'bg-pink-100 text-pink-700',
  chatbot: 'bg-teal-100 text-teal-700',
  other: 'bg-gray-100 text-gray-700',
};

function LeadSourceBadge({ source }) {
  if (!source) return null;
  const key = String(source).toLowerCase().replace(/[-\s]/g, '_');
  const cls = LEAD_SOURCE_COLORS[key] || 'bg-gray-100 text-gray-700';
  const label = key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  return (
    <span className={`inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded ${cls}`} data-testid={`lead-source-${key}`}>
      {label}
    </span>
  );
}

const TIME_SLOT_PRESETS = [
  { start: '08:00', end: '10:00', label: '8–10 AM' },
  { start: '10:00', end: '12:00', label: '10–12' },
  { start: '12:00', end: '14:00', label: '12–2 PM' },
  { start: '14:00', end: '16:00', label: '2–4 PM' },
  { start: '16:00', end: '18:00', label: '4–6 PM' },
  { start: '18:00', end: '20:00', label: '6–8 PM' },
];

function ScheduleRow({ booking: b, technicians, onReassign, onReschedule, onSlotChange }) {
  const slotKey = b.time_slot_start && b.time_slot_end ? `${b.time_slot_start}-${b.time_slot_end}` : 'custom';
  return (
    <div className="px-4 py-3 grid grid-cols-1 md:grid-cols-[1fr_180px_160px_140px_120px] gap-3 items-center text-sm">
      <div>
        <div className="font-semibold text-slate-900 flex items-center gap-2 flex-wrap">
          <span>{b.customer_name || '—'}</span>
          <span className="text-[10px] font-mono text-slate-400">{b.booking_code}</span>
          <LeadSourceBadge source={b.lead_source} />
        </div>
        <div className="text-xs text-slate-500 flex items-center gap-3 flex-wrap mt-0.5">
          <span className="flex items-center gap-1"><Phone size={11} /> {b.customer_phone || '—'}</span>
          <span>{b.service_label || b.service_type || b.pest_type || '—'}</span>
          {(b.time_slot_start && b.time_slot_end) && (
            <span className="flex items-center gap-1 text-emerald-700 font-medium"><Clock size={11} /> {b.time_slot_start} – {b.time_slot_end}</span>
          )}
          <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
            b.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
              b.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                b.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                  'bg-amber-100 text-amber-700'
          }`}>
            {b.status || 'pending'}
          </span>
        </div>
      </div>
      <select
        value={b.technician_id || ''}
        onChange={(e) => onReassign(b.id, e.target.value)}
        className="h-9 px-2 rounded-md border border-slate-300 bg-white text-xs"
        data-testid={`reassign-${b.id}`}
      >
        <option value="">— unassigned —</option>
        {technicians.map(t => (
          <option key={t.id} value={t.id}>{t.name}</option>
        ))}
      </select>
      <Input
        type="date"
        value={b.scheduled_date || ''}
        onChange={(e) => onReschedule(b.id, e.target.value)}
        className="h-9 text-xs"
        data-testid={`reschedule-${b.id}`}
      />
      <select
        value={slotKey}
        onChange={(e) => {
          if (e.target.value === 'custom') return;
          const [s, en] = e.target.value.split('-');
          onSlotChange(b.id, s, en);
        }}
        className="h-9 px-2 rounded-md border border-slate-300 bg-white text-xs"
        data-testid={`slot-${b.id}`}
      >
        {TIME_SLOT_PRESETS.map(p => (
          <option key={`${p.start}-${p.end}`} value={`${p.start}-${p.end}`}>{p.label}</option>
        ))}
        <option value="custom">Custom…</option>
      </select>
      <div className="text-right text-xs">
        <div className="font-bold text-emerald-700">₹{(b.final_amount || 0).toLocaleString('en-IN')}</div>
      </div>
    </div>
  );
}

// ============================================================
// (b) QUOTATIONS
// ============================================================
const EMPTY_QUOTE = {
  customer_name: '', customer_phone: '', customer_email: '', customer_address: '',
  service_type: '', line_items: [{ description: '', qty: 1, unit_price: 0, discount_pct: 0 }],
  tax_pct: 18, notes: '', valid_until: '',
  sector: 'residential', business_name: '', contact_person: '', reference_number: '', scope_of_work: '', treatment_methodology: '', exclusions: '', warranty_conditions: '', payment_terms: '', acceptance_text: 'I accept this quotation and authorize the work described above.',
};

export function QuotationsTab({ api }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);  // null | {} | quote

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await api('GET', '/admin/quotations'); setRows(res.data || []); }
    catch (e) { /* */ } finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const downloadPdf = async (q) => {
    try {
      // Direct fetch w/ Bearer to grab blob (api wrapper doesn't support responseType)
      const token = localStorage.getItem('entorid_panel_token') || '';
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/panel/admin/quotations/${q.id}/pdf`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const dl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = dl; a.download = `Quotation-${q.quote_number}.pdf`; a.click();
      URL.revokeObjectURL(dl);
    } catch (e) { alert('Failed to fetch PDF: ' + e.message); }
  };

  const sendWa = async (q) => {
    try {
      const res = await api('POST', `/admin/quotations/${q.id}/send-wa`);
      window.open(res.data.wa_url, '_blank');
      await load();
    } catch (e) { alert('Failed: ' + (e.response?.data?.detail || e.message)); }
  };

  const remove = async (q) => {
    if (!window.confirm(`Delete quotation ${q.quote_number}?`)) return;
    try { await api('DELETE', `/admin/quotations/${q.id}`); await load(); }
    catch (e) { alert('Failed: ' + (e.response?.data?.detail || e.message)); }
  };

  return (
    <div data-testid="quotations-tab">
      <SectionHeader
        title="Quotations"
        subtitle="Create, share, and track customer quotations · auto-generated PDFs"
        actions={
          <Button onClick={() => setEditing(EMPTY_QUOTE)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="new-quote-btn">
            <Plus size={16} className="mr-1.5" /> New Quotation
          </Button>
        }
      />
      {loading && <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>}
      {!loading && rows.length === 0 && <EmptyState icon={FileText} title="No quotations yet" hint="Create your first quotation to share via WhatsApp or download as PDF." />}

      {rows.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3">Quote #</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Service</th>
                <th className="px-4 py-3 text-right">Total</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Valid Until</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map(q => (
                <tr key={q.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-mono text-xs">{q.quote_number}</td>
                  <td className="px-4 py-3">
                    <div className="font-semibold">{q.customer_name}</div>
                    <div className="text-xs text-slate-500">{q.customer_phone}</div>
                  </td>
                  <td className="px-4 py-3">{q.service_type || '—'}</td>
                  <td className="px-4 py-3 text-right font-semibold">{fmtMoney(q.totals?.total)}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                      q.status === 'accepted' ? 'bg-emerald-100 text-emerald-700' :
                        q.status === 'sent' ? 'bg-blue-100 text-blue-700' :
                          q.status === 'rejected' ? 'bg-red-100 text-red-700' :
                            'bg-slate-100 text-slate-700'
                    }`}>
                      {q.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs">{fmtDate(q.valid_until)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => downloadPdf(q)} title="Download PDF" data-testid={`quote-pdf-${q.id}`}><Download size={14} /></Button>
                      <Button variant="ghost" size="sm" onClick={() => sendWa(q)} title="Share on WhatsApp" data-testid={`quote-wa-${q.id}`}><Send size={14} /></Button>
                      <Button variant="ghost" size="sm" onClick={() => setEditing(q)} title="Edit"><Edit2 size={14} /></Button>
                      <Button variant="ghost" size="sm" onClick={() => remove(q)} title="Delete" className="text-red-600"><Trash2 size={14} /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <QuoteEditor open={!!editing} onClose={() => setEditing(null)} initial={editing} api={api} onSaved={load} />
    </div>
  );
}

function QuoteEditor({ open, onClose, initial, api, onSaved }) {
  const [form, setForm] = useState(EMPTY_QUOTE);
  const [saving, setSaving] = useState(false);
  const isEdit = !!(initial && initial.id);

  useEffect(() => {
    if (open) {
      setForm({
        ...EMPTY_QUOTE,
        ...(initial || {}),
        line_items: (initial?.line_items?.length ? initial.line_items : EMPTY_QUOTE.line_items),
      });
    }
  }, [open, initial]);

  const totals = useMemo(() => {
    let sub = 0, disc = 0;
    (form.line_items || []).forEach(li => {
      const g = Number(li.qty || 0) * Number(li.unit_price || 0);
      sub += g;
      disc += g * Number(li.discount_pct || 0) / 100;
    });
    const after = sub - disc;
    const tax = after * Number(form.tax_pct || 0) / 100;
    return { sub, disc, after, tax, total: after + tax };
  }, [form.line_items, form.tax_pct]);

  const updateLine = (idx, patch) => {
    setForm(f => ({
      ...f,
      line_items: f.line_items.map((li, i) => i === idx ? { ...li, ...patch } : li),
    }));
  };
  const addLine = () => setForm(f => ({ ...f, line_items: [...f.line_items, { description: '', qty: 1, unit_price: 0, discount_pct: 0 }] }));
  const removeLine = (idx) => setForm(f => ({ ...f, line_items: f.line_items.filter((_, i) => i !== idx) }));

  const save = async () => {
    if (!form.customer_name || !form.customer_phone) { alert('Customer name and phone are required'); return; }
    if (!form.line_items?.length) { alert('At least one line item is required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        line_items: form.line_items.map(li => ({
          description: li.description,
          qty: Number(li.qty || 0),
          unit_price: Number(li.unit_price || 0),
          discount_pct: Number(li.discount_pct || 0),
        })),
        tax_pct: Number(form.tax_pct || 0),
      };
      if (isEdit) await api('PATCH', `/admin/quotations/${initial.id}`, payload);
      else await api('POST', `/admin/quotations`, payload);
      onSaved();
      onClose();
    } catch (e) { alert('Failed: ' + (e.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };

  return (
    <Modal
      open={open} onClose={onClose} wide
      title={isEdit ? `Edit ${initial.quote_number}` : 'New Quotation'}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="quote-save-btn">
            {saving ? <Loader2 size={14} className="animate-spin" /> : 'Save'}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div><Label>Customer Name *</Label><Input value={form.customer_name || ''} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="quote-name" /></div>
        <div><Label>Phone *</Label><Input value={form.customer_phone || ''} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} data-testid="quote-phone" /></div>
        <div><Label>Email</Label><Input value={form.customer_email || ''} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} /></div>
        <div><Label>Service Type</Label><Input value={form.service_type || ''} onChange={(e) => setForm({ ...form, service_type: e.target.value })} /></div>
        <div><Label>Sector *</Label><select value={form.sector || 'residential'} onChange={(e) => setForm({ ...form, sector: e.target.value })} className="mt-1 h-10 w-full border border-slate-300 bg-white px-3 text-sm" data-testid="quote-sector"><option value="residential">Residential</option><option value="commercial">Commercial</option><option value="industrial">Industrial</option></select></div>
        <div><Label>Business / Site Name</Label><Input value={form.business_name || ''} onChange={(e) => setForm({ ...form, business_name: e.target.value })} data-testid="quote-business-name" /></div>
        <div><Label>Contact Person</Label><Input value={form.contact_person || ''} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} data-testid="quote-contact-person" /></div>
        <div><Label>Customer Reference No.</Label><Input value={form.reference_number || ''} onChange={(e) => setForm({ ...form, reference_number: e.target.value })} data-testid="quote-reference-number" /></div>
        <div className="md:col-span-2"><Label>Address</Label><Input value={form.customer_address || ''} onChange={(e) => setForm({ ...form, customer_address: e.target.value })} /></div>
      </div>

      <div className="mt-5">
        <div className="flex justify-between items-center mb-2">
          <Label>Line Items</Label>
          <Button variant="outline" size="sm" onClick={addLine} data-testid="quote-add-line"><Plus size={14} className="mr-1" /> Add line</Button>
        </div>
        <div className="space-y-2">
          {form.line_items.map((li, idx) => (
            <div key={idx} className="grid grid-cols-[1fr_60px_90px_60px_24px] gap-2 items-center">
              <Input placeholder="Description" value={li.description} onChange={(e) => updateLine(idx, { description: e.target.value })} />
              <Input type="number" step="any" placeholder="Qty" value={li.qty} onChange={(e) => updateLine(idx, { qty: e.target.value })} />
              <Input type="number" step="any" placeholder="Price" value={li.unit_price} onChange={(e) => updateLine(idx, { unit_price: e.target.value })} />
              <Input type="number" step="any" placeholder="Disc %" value={li.discount_pct} onChange={(e) => updateLine(idx, { discount_pct: e.target.value })} />
              <button onClick={() => removeLine(idx)} className="text-red-500"><X size={14} /></button>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mt-4">
        <div><Label>Tax %</Label><Input type="number" step="any" value={form.tax_pct} onChange={(e) => setForm({ ...form, tax_pct: e.target.value })} /></div>
        <div><Label>Valid Until</Label><Input type="date" value={form.valid_until || ''} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} /></div>
      </div>
      <div className="mt-3"><Label>Notes</Label><Textarea value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} /></div>
      <div className="mt-5 border-t border-slate-200 pt-4"><h4 className="font-bold text-slate-900">Professional proposal sections</h4><p className="mt-1 text-xs text-slate-500">These fields appear in the final quotation and can be tailored per sector/site.</p><div className="mt-3 grid gap-3 md:grid-cols-2"><div className="md:col-span-2"><Label>Scope of Work</Label><Textarea value={form.scope_of_work || ''} onChange={(e) => setForm({ ...form, scope_of_work: e.target.value })} rows={3} data-testid="quote-scope" /></div><div><Label>Treatment Methodology</Label><Textarea value={form.treatment_methodology || ''} onChange={(e) => setForm({ ...form, treatment_methodology: e.target.value })} rows={3} data-testid="quote-methodology" /></div><div><Label>Warranty Conditions</Label><Textarea value={form.warranty_conditions || ''} onChange={(e) => setForm({ ...form, warranty_conditions: e.target.value })} rows={3} data-testid="quote-warranty" /></div><div><Label>Exclusions</Label><Textarea value={form.exclusions || ''} onChange={(e) => setForm({ ...form, exclusions: e.target.value })} rows={3} data-testid="quote-exclusions" /></div><div><Label>Payment Terms</Label><Textarea value={form.payment_terms || ''} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} rows={3} data-testid="quote-payment-terms" /></div><div className="md:col-span-2"><Label>Customer Acceptance Text</Label><Textarea value={form.acceptance_text || ''} onChange={(e) => setForm({ ...form, acceptance_text: e.target.value })} rows={2} data-testid="quote-acceptance" /></div></div></div>

      <div className="mt-4 bg-slate-50 rounded-xl p-3 text-sm">
        <div className="flex justify-between"><span>Subtotal</span><span>{fmtMoney(totals.sub)}</span></div>
        <div className="flex justify-between text-emerald-700"><span>Discount</span><span>− {fmtMoney(totals.disc)}</span></div>
        <div className="flex justify-between text-slate-500"><span>Tax</span><span>{fmtMoney(totals.tax)}</span></div>
        <div className="flex justify-between font-bold text-base mt-1 pt-1 border-t border-slate-200"><span>Total</span><span>{fmtMoney(totals.total)}</span></div>
      </div>
    </Modal>
  );
}

// ============================================================
// (c) WORK ORDERS (admin viewer)
// ============================================================
export function WorkOrdersTab({ api }) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api('GET', `/admin/bookings`);
      setBookings((res.data || []).filter(b => b.technician_id || b.work_order));
    } catch (e) { /* */ } finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="work-orders-tab">
      <SectionHeader
        title="Work Orders"
        subtitle="Job sheets · before/after photos · technician completion notes"
        actions={<Button variant="outline" size="sm" onClick={load}><RefreshCw size={14} className="mr-1.5" /> Refresh</Button>}
      />
      {loading && <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>}
      {!loading && bookings.length === 0 && <EmptyState icon={Wrench} title="No active work orders" hint="Assign bookings to technicians to start work orders." />}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {bookings.map(b => {
          const wo = b.work_order || {};
          const photos = wo.photos || [];
          return (
            <div key={b.id} className="bg-white border border-slate-200 rounded-2xl p-4 hover:shadow-md transition-shadow" data-testid={`wo-card-${b.id}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="font-mono text-xs text-slate-400">{b.booking_code || b.id.slice(0, 8)}</div>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                  wo.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                    wo.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                      'bg-amber-100 text-amber-700'
                }`}>{wo.status || (b.technician_id ? 'assigned' : 'no-wo')}</span>
              </div>
              <div className="font-semibold text-slate-900">{b.customer_name}</div>
              <div className="text-xs text-slate-500 mb-2">{b.service_type || b.pest_type} · {b.customer_phone}</div>
              <div className="text-xs text-slate-500">Tech: <span className="text-slate-900 font-medium">{b.technician_name || '—'}</span></div>
              <div className="text-xs text-slate-500">Date: <span className="text-slate-900">{fmtDate(b.scheduled_date)}</span></div>
              {photos.length > 0 && (
                <div className="mt-2 flex gap-1 flex-wrap">
                  {photos.slice(0, 4).map(p => (
                    <div key={p.id} className="relative w-12 h-12 rounded overflow-hidden border border-slate-200">
                      <img src={p.url} alt={p.kind} className="w-full h-full object-cover" />
                      <span className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[8px] text-center">{p.kind}</span>
                    </div>
                  ))}
                  {photos.length > 4 && <div className="w-12 h-12 rounded bg-slate-100 flex items-center justify-center text-xs font-semibold text-slate-600">+{photos.length - 4}</div>}
                </div>
              )}
              <Button variant="outline" size="sm" className="w-full mt-3" onClick={() => setSelected(b)} data-testid={`wo-view-${b.id}`}>
                <Eye size={13} className="mr-1.5" /> View Details
              </Button>
            </div>
          );
        })}
      </div>

      <Modal open={!!selected} onClose={() => setSelected(null)} title={`Work Order · ${selected?.booking_code || ''}`} wide>
        {selected && (
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Customer</Label><div className="font-semibold">{selected.customer_name}</div><div className="text-xs text-slate-500">{selected.customer_phone}</div></div>
              <div><Label className="text-xs">Technician</Label><div className="font-semibold">{selected.technician_name || '—'}</div></div>
              <div><Label className="text-xs">Service</Label><div>{selected.service_type || selected.pest_type}</div></div>
              <div><Label className="text-xs">Scheduled</Label><div>{fmtDate(selected.scheduled_date)}</div></div>
              <div><Label className="text-xs">Status</Label><div>{selected.work_order?.status || selected.status || 'pending'}</div></div>
              <div><Label className="text-xs">Amount</Label><div>{fmtMoney(selected.amount)}</div></div>
            </div>
            {selected.work_order && (
              <>
                <div className="border-t pt-3">
                  <Label className="text-xs">Areas Treated</Label><div className="text-slate-700">{selected.work_order.areas_treated || '—'}</div>
                  <Label className="text-xs mt-2 block">Chemicals Used</Label><div className="text-slate-700">{selected.work_order.chemicals_used || '—'}</div>
                  <Label className="text-xs mt-2 block">Observations</Label><div className="text-slate-700">{selected.work_order.observations || '—'}</div>
                  <Label className="text-xs mt-2 block">Completion Notes</Label><div className="text-slate-700">{selected.work_order.completion_notes || '—'}</div>
                </div>
                {(selected.work_order.photos || []).length > 0 && (
                  <div className="border-t pt-3">
                    <Label className="text-xs">Photos</Label>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-2">
                      {selected.work_order.photos.map(p => (
                        <a key={p.id} href={p.url} target="_blank" rel="noreferrer" className="relative aspect-square rounded-lg overflow-hidden border border-slate-200 block">
                          <img src={p.url} alt={p.kind} className="w-full h-full object-cover" />
                          <span className="absolute top-1 left-1 bg-black/70 text-white text-[10px] px-1.5 py-0.5 rounded uppercase">{p.kind}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

// ============================================================
// (d) AMC CONTRACTS
// ============================================================
const EMPTY_AMC = {
  customer_name: '', customer_phone: '', customer_email: '', customer_address: '',
  service_type: 'General Pest', plan_name: 'Annual', amount: 12000, visits_per_year: 4,
  start_date: new Date().toISOString().slice(0, 10),
  end_date: new Date(Date.now() + 365 * 86400000).toISOString().slice(0, 10),
};

export function AMCTab({ api }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [visitsFor, setVisitsFor] = useState(null);   // AMC whose visits we're managing

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await api('GET', `/admin/amc`); setRows(res.data || []); }
    catch (e) { /* */ } finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const remove = async (a) => {
    if (!window.confirm(`Delete AMC ${a.contract_number}?`)) return;
    try { await api('DELETE', `/admin/amc/${a.id}`); await load(); }
    catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  return (
    <div data-testid="amc-tab">
      <SectionHeader
        title="AMC Contracts"
        subtitle="Annual maintenance plans · auto-flagged when expiring within 30 days"
        actions={<Button onClick={() => setEditing(EMPTY_AMC)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="new-amc-btn"><Plus size={16} className="mr-1.5" /> New AMC</Button>}
      />
      {loading && <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>}
      {!loading && rows.length === 0 && <EmptyState icon={RefreshCw} title="No AMC contracts yet" hint="Convert one-time customers to recurring AMC contracts." />}
      {rows.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3">Contract</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Plan</th>
                <th className="px-4 py-3">Visits</th>
                <th className="px-4 py-3">End Date</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map(a => {
                const daysLeft = a.end_date ? Math.ceil((new Date(a.end_date) - new Date()) / 86400000) : null;
                return (
                  <tr key={a.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-mono text-xs">{a.contract_number}</td>
                    <td className="px-4 py-3">
                      <div className="font-semibold">{a.customer_name}</div>
                      <div className="text-xs text-slate-500">{a.customer_phone}</div>
                    </td>
                    <td className="px-4 py-3">{a.plan_name}<div className="text-xs text-slate-500">{fmtMoney(a.amount)}</div></td>
                    <td className="px-4 py-3">{a.visits_used || 0}/{a.visits_per_year}</td>
                    <td className="px-4 py-3">
                      <div>{fmtDate(a.end_date)}</div>
                      {daysLeft !== null && a.status === 'active' && (
                        <div className={`text-xs ${daysLeft <= 30 ? 'text-amber-700 font-semibold' : 'text-slate-500'}`}>
                          {daysLeft > 0 ? `${daysLeft}d left` : 'expired'}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                        a.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                          a.status === 'expired' ? 'bg-red-100 text-red-700' :
                            'bg-slate-100 text-slate-700'
                      }`}>{a.status}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => setVisitsFor(a)} title="Manage Visits" className="text-indigo-600" data-testid={`amc-visits-${a.id}`}><CalendarDays size={14} /></Button>
                        <Button variant="ghost" size="sm" onClick={() => setEditing(a)} title="Edit"><Edit2 size={14} /></Button>
                        <Button variant="ghost" size="sm" onClick={() => remove(a)} title="Delete" className="text-red-600"><Trash2 size={14} /></Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <AMCEditor open={!!editing} onClose={() => setEditing(null)} initial={editing} api={api} onSaved={load} />
      {visitsFor && <AMCVisitsDrawer amc={visitsFor} api={api} onClose={() => setVisitsFor(null)} onChanged={(updated) => { setVisitsFor(updated); load(); }} />}
    </div>
  );
}


// =====================================================
// AMC VISITS DRAWER — schedule, edit, mark-complete, reschedule, delete visits
// All visit data round-trips against /admin/amc/{id}/visit(/no) endpoints.
// =====================================================
const VISIT_PESTS = ['General Pest Control', 'Cockroach', 'Termite', 'Bed Bug', 'Rodent', 'Mosquito', 'Ant', 'Wood Borer'];
const VISIT_STATUS_TONE = {
  scheduled: 'bg-blue-100 text-blue-700',
  completed: 'bg-emerald-100 text-emerald-700',
  rescheduled: 'bg-amber-100 text-amber-800',
  skipped: 'bg-slate-200 text-slate-700',
  cancelled: 'bg-rose-100 text-rose-700',
};
const EMPTY_VISIT = {
  scheduled_date: new Date().toISOString().slice(0, 10),
  scheduled_time_slot: '',
  pest_types: [],
  visit_type: 'regular',
  technician_name: '',
  notes: '',
  status: 'scheduled',
  completion_notes: '',
  chemicals_used: '',
};

function AMCVisitsDrawer({ amc, api, onClose, onChanged }) {
  const [editing, setEditing] = useState(null); // visit object or 'new'
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const visits = (amc.visits || []).slice().sort((a, b) => (b.scheduled_date || '').localeCompare(a.scheduled_date || ''));
  const remaining = (amc.visits_per_year || 0) - (amc.visits_used || 0);

  const saveVisit = async (visit) => {
    setBusy(true); setErr('');
    try {
      let updated;
      if (visit.visit_no) {
        const r = await api('PATCH', `/admin/amc/${amc.id}/visit/${visit.visit_no}`, visit);
        updated = r.data;
      } else {
        const r = await api('POST', `/admin/amc/${amc.id}/visit`, visit);
        updated = r.data;
      }
      setEditing(null);
      onChanged(updated);
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const deleteVisit = async (v) => {
    if (!window.confirm(`Delete visit #${v.visit_no} (${v.scheduled_date})?`)) return;
    setBusy(true);
    try {
      const r = await api('DELETE', `/admin/amc/${amc.id}/visit/${v.visit_no}`);
      onChanged(r.data);
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[85] bg-black/40 backdrop-blur-sm" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="amc-visits-drawer">
      <div className="absolute right-0 top-0 h-full w-full sm:w-[640px] bg-white shadow-2xl overflow-hidden flex flex-col">
        <div className="px-5 py-3.5 bg-emerald-700 text-white flex items-center justify-between">
          <div>
            <div className="text-xs opacity-90 flex items-center gap-1"><CalendarDays size={11} /> AMC Visit Schedule</div>
            <div className="font-bold font-mono">{amc.contract_number}</div>
            <div className="text-xs opacity-90 mt-0.5">{amc.customer_name} · {amc.plan_name}</div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center" data-testid="amc-visits-close"><X size={18} /></button>
        </div>

        <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
          <div className="text-sm">
            <b className="text-emerald-700">{amc.visits_used || 0}</b> done · <b className="text-slate-700">{Math.max(0, remaining)}</b> remaining of <b>{amc.visits_per_year}</b>
          </div>
          <Button onClick={() => setEditing({ ...EMPTY_VISIT })} size="sm" className="bg-emerald-600 hover:bg-emerald-700" data-testid="amc-visit-add"><Plus size={14} className="mr-1" /> Schedule Visit</Button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-100 rounded p-2"><AlertCircle size={14} className="inline mr-1" />{err}</div>}
          {visits.length === 0 && <div className="text-center py-10 text-slate-400 text-sm">No visits scheduled. Click <b>Schedule Visit</b> to add the first one.</div>}
          {visits.map((v) => {
            const tone = VISIT_STATUS_TONE[v.status] || VISIT_STATUS_TONE.scheduled;
            return (
              <div key={v.visit_no} className="bg-white border border-slate-200 rounded-lg p-3" data-testid={`amc-visit-${v.visit_no}`}>
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-xs font-bold">Visit #{v.visit_no}</span>
                      <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded ${tone}`}>{(v.status || '').replace(/_/g, ' ')}</span>
                      {v.visit_type && v.visit_type !== 'regular' && <span className="text-[10px] uppercase font-semibold px-2 py-0.5 rounded bg-indigo-50 text-indigo-700">{v.visit_type}</span>}
                    </div>
                    <div className="text-sm font-semibold mt-1">📅 {fmtDate(v.scheduled_date)}{v.scheduled_time_slot && <> · ⏰ {v.scheduled_time_slot}</>}</div>
                    {(v.pest_types || []).length > 0 && (
                      <div className="text-xs text-slate-600 mt-1"><Bug size={11} className="inline mr-1" />{(v.pest_types || []).join(', ')}</div>
                    )}
                    {v.technician_name && <div className="text-xs text-slate-500 mt-0.5">👷 {v.technician_name}</div>}
                    {v.chemicals_used && <div className="text-xs text-slate-500">🧪 {v.chemicals_used}</div>}
                    {v.notes && <div className="text-xs text-slate-600 mt-1 italic">📝 {v.notes}</div>}
                    {v.completion_notes && <div className="mt-1 bg-emerald-50 border border-emerald-200 rounded p-2 text-xs"><b className="text-emerald-800">✅ Completion: </b>{v.completion_notes}</div>}
                  </div>
                  <div className="inline-flex gap-1 shrink-0">
                    <Button variant="ghost" size="sm" onClick={() => setEditing(v)} title="Edit" data-testid={`amc-visit-edit-${v.visit_no}`}><Edit2 size={13} /></Button>
                    <Button variant="ghost" size="sm" onClick={() => deleteVisit(v)} title="Delete" className="text-rose-600" data-testid={`amc-visit-del-${v.visit_no}`}><Trash2 size={13} /></Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {editing && <AMCVisitForm visit={editing} onClose={() => setEditing(null)} onSave={saveVisit} busy={busy} />}
    </div>
  );
}

function AMCVisitForm({ visit, onClose, onSave, busy }) {
  const [form, setForm] = useState(visit);
  const togglePest = (p) => setForm((f) => {
    const s = new Set(f.pest_types || []);
    if (s.has(p)) s.delete(p); else s.add(p);
    return { ...f, pest_types: Array.from(s) };
  });
  const submit = (e) => { e.preventDefault(); onSave(form); };

  return (
    <div className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="amc-visit-form">
      <form onSubmit={submit} className="bg-white w-full sm:max-w-lg rounded-2xl overflow-hidden max-h-[92vh] flex flex-col">
        <div className="px-5 py-3.5 bg-indigo-700 text-white flex items-center justify-between">
          <div className="font-bold">{form.visit_no ? `Edit Visit #${form.visit_no}` : 'Schedule New Visit'}</div>
          <button type="button" onClick={onClose} className="w-8 h-8 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3 overflow-y-auto">
          <div className="grid grid-cols-2 gap-2">
            <div><label className="text-xs font-semibold">Date *</label><input type="date" required value={form.scheduled_date} onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })} className="mt-1 h-10 w-full rounded-md border border-slate-300 px-2 text-sm" data-testid="amc-vform-date" /></div>
            <div><label className="text-xs font-semibold">Time Slot</label><input value={form.scheduled_time_slot || ''} onChange={(e) => setForm({ ...form, scheduled_time_slot: e.target.value })} placeholder="e.g. 10 AM - 12 PM" className="mt-1 h-10 w-full rounded-md border border-slate-300 px-2 text-sm" data-testid="amc-vform-slot" /></div>
          </div>
          <div>
            <label className="text-xs font-semibold">Pest Types (multi-select)</label>
            <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="amc-vform-pests">
              {VISIT_PESTS.map((p) => {
                const on = (form.pest_types || []).includes(p);
                return <button key={p} type="button" onClick={() => togglePest(p)} className={`px-2.5 py-1 rounded-full text-[11px] font-medium ${on ? 'bg-emerald-600 text-white' : 'bg-white border border-slate-300 text-slate-700'}`}>{p}</button>;
              })}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-semibold">Visit Type</label>
              <select value={form.visit_type || 'regular'} onChange={(e) => setForm({ ...form, visit_type: e.target.value })} className="mt-1 h-10 w-full rounded-md border border-slate-300 px-2 text-sm bg-white" data-testid="amc-vform-type">
                <option value="regular">Regular</option>
                <option value="callback">Callback / Re-treatment</option>
                <option value="inspection">Inspection only</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold">Status</label>
              <select value={form.status || 'scheduled'} onChange={(e) => setForm({ ...form, status: e.target.value })} className="mt-1 h-10 w-full rounded-md border border-slate-300 px-2 text-sm bg-white" data-testid="amc-vform-status">
                <option value="scheduled">Scheduled</option>
                <option value="completed">Completed</option>
                <option value="rescheduled">Rescheduled</option>
                <option value="skipped">Skipped</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>
          <div><label className="text-xs font-semibold">Technician Name</label><input value={form.technician_name || ''} onChange={(e) => setForm({ ...form, technician_name: e.target.value })} className="mt-1 h-10 w-full rounded-md border border-slate-300 px-2 text-sm" data-testid="amc-vform-tech" /></div>
          <div><label className="text-xs font-semibold">Chemicals Used</label><input value={form.chemicals_used || ''} onChange={(e) => setForm({ ...form, chemicals_used: e.target.value })} placeholder="e.g. Termidor SC 9.1%" className="mt-1 h-10 w-full rounded-md border border-slate-300 px-2 text-sm" data-testid="amc-vform-chem" /></div>
          <div><label className="text-xs font-semibold">Notes / Pre-visit Instructions</label><textarea rows={2} value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm" data-testid="amc-vform-notes" /></div>
          {form.status === 'completed' && (
            <div><label className="text-xs font-semibold">Completion Notes</label><textarea rows={2} value={form.completion_notes || ''} onChange={(e) => setForm({ ...form, completion_notes: e.target.value })} placeholder="What was done; observations" className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm" data-testid="amc-vform-comp" /></div>
          )}
        </div>
        <div className="px-5 py-3 bg-slate-50 border-t flex gap-2 justify-end">
          <Button type="button" variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button type="submit" className="bg-indigo-700 hover:bg-indigo-800" disabled={busy} data-testid="amc-vform-save">{busy ? <Loader2 size={13} className="animate-spin mr-1" /> : null} {form.visit_no ? 'Save Changes' : 'Schedule Visit'}</Button>
        </div>
      </form>
    </div>
  );
}

function AMCEditor({ open, onClose, initial, api, onSaved }) {
  const [form, setForm] = useState(EMPTY_AMC);
  const [saving, setSaving] = useState(false);
  const isEdit = !!(initial && initial.id);
  useEffect(() => { if (open) setForm({ ...EMPTY_AMC, ...(initial || {}) }); }, [open, initial]);

  const save = async () => {
    if (!form.customer_name || !form.customer_phone) { alert('Customer name and phone required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, amount: Number(form.amount || 0), visits_per_year: Number(form.visits_per_year || 0) };
      if (isEdit) await api('PATCH', `/admin/amc/${initial.id}`, payload);
      else await api('POST', `/admin/amc`, payload);
      onSaved(); onClose();
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title={isEdit ? `Edit ${initial.contract_number}` : 'New AMC Contract'}
      footer={<><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="amc-save-btn">{saving ? <Loader2 size={14} className="animate-spin" /> : 'Save'}</Button></>}
    >
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2"><Label>Customer Name *</Label><Input value={form.customer_name || ''} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="amc-name" /></div>
        <div><Label>Phone *</Label><Input value={form.customer_phone || ''} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} data-testid="amc-phone" /></div>
        <div><Label>Email</Label><Input value={form.customer_email || ''} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} /></div>
        <div className="col-span-2"><Label>Address</Label><Input value={form.customer_address || ''} onChange={(e) => setForm({ ...form, customer_address: e.target.value })} /></div>
        <div><Label>Service Type</Label><Input value={form.service_type} onChange={(e) => setForm({ ...form, service_type: e.target.value })} /></div>
        <div><Label>Plan Name</Label><Input value={form.plan_name} onChange={(e) => setForm({ ...form, plan_name: e.target.value })} /></div>
        <div><Label>Amount ₹</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></div>
        <div><Label>Visits / Year</Label><Input type="number" value={form.visits_per_year} onChange={(e) => setForm({ ...form, visits_per_year: e.target.value })} /></div>
        <div><Label>Start Date</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
        <div><Label>End Date</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
        <div className="col-span-2"><Label>Notes</Label><Textarea value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} /></div>
      </div>
    </Modal>
  );
}

// ============================================================
// (e) INVENTORY
// ============================================================
const EMPTY_ITEM = { name: '', sku: '', category: 'chemical', unit: 'litre', quantity_on_hand: 0, reorder_level: 0, unit_cost: 0, supplier: '' };

export function InventoryTab({ api }) {
  const [items, setItems] = useState([]);
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [movItem, setMovItem] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api('GET', `/admin/inventory`),
        api('GET', `/admin/inventory/movements`),
      ]);
      setItems(a.data || []);
      setMovements(b.data || []);
    } catch (e) { /* */ } finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const remove = async (it) => {
    if (!window.confirm(`Delete ${it.name}?`)) return;
    try { await api('DELETE', `/admin/inventory/${it.id}`); await load(); }
    catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  return (
    <div data-testid="inventory-tab">
      <SectionHeader
        title="Inventory"
        subtitle="Chemicals · equipment · stock movements · low-stock alerts"
        actions={<Button onClick={() => setEditing(EMPTY_ITEM)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="new-item-btn"><Plus size={16} className="mr-1.5" /> New Item</Button>}
      />
      {loading && <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>}
      {!loading && items.length === 0 && <EmptyState icon={Package} title="No inventory items" hint="Track chemicals, equipment, and consumables." />}

      {items.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto mb-6">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3">Item</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3 text-right">On Hand</th>
                <th className="px-4 py-3 text-right">Reorder At</th>
                <th className="px-4 py-3">Unit Cost</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map(it => {
                const low = Number(it.quantity_on_hand || 0) <= Number(it.reorder_level || 0);
                return (
                  <tr key={it.id} className={`hover:bg-slate-50 ${low ? 'bg-amber-50/50' : ''}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold">{it.name}</div>
                      <div className="text-xs text-slate-500">{it.sku || '—'}</div>
                    </td>
                    <td className="px-4 py-3 capitalize">{it.category}</td>
                    <td className="px-4 py-3 text-right">
                      <div className={`font-semibold ${low ? 'text-amber-700' : 'text-slate-900'}`}>{it.quantity_on_hand} {it.unit}</div>
                      {low && <div className="text-[10px] text-amber-700 font-bold">LOW</div>}
                    </td>
                    <td className="px-4 py-3 text-right">{it.reorder_level}</td>
                    <td className="px-4 py-3">{fmtMoney(it.unit_cost)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => setMovItem(it)} title="Stock movement" data-testid={`mov-${it.id}`}><ArrowDown size={14} /></Button>
                        <Button variant="ghost" size="sm" onClick={() => setEditing(it)} title="Edit"><Edit2 size={14} /></Button>
                        <Button variant="ghost" size="sm" onClick={() => remove(it)} className="text-red-600" title="Delete"><Trash2 size={14} /></Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {movements.length > 0 && (
        <div>
          <h3 className="font-semibold text-slate-900 mb-2">Recent stock movements</h3>
          <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-4 py-2">When</th>
                  <th className="px-4 py-2">Item</th>
                  <th className="px-4 py-2">Direction</th>
                  <th className="px-4 py-2 text-right">Qty</th>
                  <th className="px-4 py-2 text-right">Balance</th>
                  <th className="px-4 py-2">Reason</th>
                  <th className="px-4 py-2">By</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {movements.slice(0, 20).map(m => (
                  <tr key={m.id}>
                    <td className="px-4 py-2 text-xs">{(m.created_at || '').slice(0, 16).replace('T', ' ')}</td>
                    <td className="px-4 py-2">{m.item_name}</td>
                    <td className="px-4 py-2"><span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${m.direction === 'in' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>{m.direction.toUpperCase()}</span></td>
                    <td className="px-4 py-2 text-right">{m.quantity}</td>
                    <td className="px-4 py-2 text-right">{m.balance_after}</td>
                    <td className="px-4 py-2 text-xs text-slate-600">{m.reason || '—'}</td>
                    <td className="px-4 py-2 text-xs">{m.by}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <InventoryEditor open={!!editing} onClose={() => setEditing(null)} initial={editing} api={api} onSaved={load} />
      <MovementEditor open={!!movItem} onClose={() => setMovItem(null)} item={movItem} api={api} onSaved={load} />
    </div>
  );
}

function InventoryEditor({ open, onClose, initial, api, onSaved }) {
  const [form, setForm] = useState(EMPTY_ITEM);
  const [saving, setSaving] = useState(false);
  const isEdit = !!(initial && initial.id);
  useEffect(() => { if (open) setForm({ ...EMPTY_ITEM, ...(initial || {}) }); }, [open, initial]);

  const save = async () => {
    if (!form.name) { alert('Name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        quantity_on_hand: Number(form.quantity_on_hand || 0),
        reorder_level: Number(form.reorder_level || 0),
        unit_cost: Number(form.unit_cost || 0),
      };
      if (isEdit) {
        // quantity_on_hand can't be patched directly — use a movement instead
        const { quantity_on_hand, ...rest } = payload;
        await api('PATCH', `/admin/inventory/${initial.id}`, rest);
      } else {
        await api('POST', `/admin/inventory`, payload);
      }
      onSaved(); onClose();
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title={isEdit ? `Edit ${initial?.name}` : 'New Inventory Item'}
      footer={<><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="item-save-btn">{saving ? <Loader2 size={14} className="animate-spin" /> : 'Save'}</Button></>}
    >
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2"><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="item-name" /></div>
        <div><Label>SKU</Label><Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} /></div>
        <div><Label>Category</Label>
          <select className="h-10 w-full px-3 rounded-md border border-slate-300 bg-white text-sm" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
            <option value="chemical">Chemical</option><option value="equipment">Equipment</option><option value="consumable">Consumable</option><option value="other">Other</option>
          </select>
        </div>
        <div><Label>Unit</Label><Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} /></div>
        <div><Label>Quantity {isEdit && <span className="text-[10px] text-slate-400">(use Movement to change)</span>}</Label><Input type="number" disabled={isEdit} value={form.quantity_on_hand} onChange={(e) => setForm({ ...form, quantity_on_hand: e.target.value })} /></div>
        <div><Label>Reorder Level</Label><Input type="number" value={form.reorder_level} onChange={(e) => setForm({ ...form, reorder_level: e.target.value })} /></div>
        <div><Label>Unit Cost ₹</Label><Input type="number" value={form.unit_cost} onChange={(e) => setForm({ ...form, unit_cost: e.target.value })} /></div>
        <div className="col-span-2"><Label>Supplier</Label><Input value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} /></div>
      </div>
    </Modal>
  );
}

function MovementEditor({ open, onClose, item, api, onSaved }) {
  const [direction, setDirection] = useState('in');
  const [qty, setQty] = useState(1);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setDirection('in'); setQty(1); setReason(''); } }, [open]);

  const save = async () => {
    setSaving(true);
    try {
      await api('POST', `/admin/inventory/movement`, {
        item_id: item.id, direction, quantity: Number(qty), reason,
      });
      onSaved(); onClose();
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setSaving(false); }
  };

  if (!item) return null;
  return (
    <Modal open={open} onClose={onClose} title={`Stock movement · ${item.name}`}
      footer={<><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="mov-save-btn">{saving ? <Loader2 size={14} className="animate-spin" /> : 'Save'}</Button></>}
    >
      <div className="space-y-3">
        <div className="text-sm text-slate-600">Current stock: <span className="font-semibold text-slate-900">{item.quantity_on_hand} {item.unit}</span></div>
        <div>
          <Label>Direction</Label>
          <div className="flex gap-2 mt-1">
            <button onClick={() => setDirection('in')} className={`flex-1 px-3 py-2 rounded-lg text-sm font-semibold ${direction === 'in' ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-600'}`}><ArrowDown size={14} className="inline mr-1" /> Stock In</button>
            <button onClick={() => setDirection('out')} className={`flex-1 px-3 py-2 rounded-lg text-sm font-semibold ${direction === 'out' ? 'bg-rose-600 text-white' : 'bg-slate-100 text-slate-600'}`}><ArrowUp size={14} className="inline mr-1" /> Stock Out</button>
          </div>
        </div>
        <div><Label>Quantity</Label><Input type="number" step="any" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="mov-qty" /></div>
        <div><Label>Reason</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="purchase / wastage / job:XX" /></div>
      </div>
    </Modal>
  );
}

// ============================================================
// (f) WHATSAPP AUTOMATIONS
// ============================================================
export function WhatsAppTab({ api }) {
  const [templates, setTemplates] = useState([]);
  const [queue, setQueue] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [activeView, setActiveView] = useState('queue'); // queue | templates | log

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, q, m] = await Promise.all([
        api('GET', `/admin/wa-templates`),
        api('GET', `/admin/wa-queue`),
        api('GET', `/admin/wa-messages`),
      ]);
      setTemplates(t.data || []);
      setQueue(q.data?.queue || []);
      setMessages(m.data || []);
    } catch (e) { /* */ } finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const removeTpl = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try { await api('DELETE', `/admin/wa-templates/${t.id}`); await load(); }
    catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  const sendNow = (q) => {
    // Open WhatsApp web/app via wa.me URL
    window.open(q.wa_url, '_blank');
    api('POST', `/admin/wa-send`, { phone: q.phone, variables: { body: q.body } }).then(load).catch(() => {});
  };

  return (
    <div data-testid="wa-tab">
      <SectionHeader
        title="WhatsApp Automations"
        subtitle="Templates · upcoming reminders queue · send log"
        actions={
          <>
            <Button variant="outline" size="sm" onClick={load}><RefreshCw size={14} className="mr-1.5" /> Refresh</Button>
            <Button onClick={() => setEditing({ trigger: 'manual', name: '', body: '', enabled: true })} className="bg-emerald-600 hover:bg-emerald-700" data-testid="new-tpl-btn">
              <Plus size={16} className="mr-1.5" /> New Template
            </Button>
          </>
        }
      />

      <div className="flex gap-1 border-b border-slate-200 mb-4">
        {['queue', 'templates', 'log'].map(v => (
          <button key={v}
            onClick={() => setActiveView(v)}
            className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${activeView === v ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            data-testid={`wa-view-${v}`}
          >
            {v === 'queue' && `Send Queue (${queue.length})`}
            {v === 'templates' && `Templates (${templates.length})`}
            {v === 'log' && `Send Log (${messages.length})`}
          </button>
        ))}
      </div>

      {loading && <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>}

      {activeView === 'queue' && (
        <>
          {queue.length === 0 ? <EmptyState icon={MessageCircle} title="No pending reminders" hint="Reminders auto-generate from tomorrow's bookings, warranties expiring in 7 days, and AMCs expiring in 30 days." /> : (
            <div className="space-y-2">
              {queue.map((q, i) => (
                <div key={i} className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col md:flex-row md:items-center gap-3" data-testid={`wa-q-${i}`}>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">{q.type.replace('_', ' ')}</span>
                      <span className="text-xs text-slate-500">{q.phone}</span>
                    </div>
                    <div className="text-sm text-slate-700 whitespace-pre-line">{q.body}</div>
                  </div>
                  <Button onClick={() => sendNow(q)} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`wa-send-${i}`}>
                    <Send size={14} className="mr-1.5" /> Send via WA
                  </Button>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {activeView === 'templates' && (
        <>
          {templates.length === 0 ? <EmptyState icon={MessageCircle} title="No templates yet" /> : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {templates.map(t => (
                <div key={t.id} className="bg-white border border-slate-200 rounded-xl p-4">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div>
                      <div className="font-semibold text-slate-900">{t.name}</div>
                      <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{t.trigger.replace('_', ' ')}</span>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => setEditing(t)}><Edit2 size={14} /></Button>
                      <Button variant="ghost" size="sm" onClick={() => removeTpl(t)} className="text-red-600"><Trash2 size={14} /></Button>
                    </div>
                  </div>
                  <div className="text-sm text-slate-600 whitespace-pre-line bg-slate-50 rounded-lg p-2">{t.body}</div>
                  <div className="mt-2 text-[10px] text-slate-400">Placeholders: {'{{name}} {{date}} {{service}} {{phone}} {{amount}} {{link}}'}</div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {activeView === 'log' && (
        <>
          {messages.length === 0 ? <EmptyState icon={MessageCircle} title="No messages sent yet" /> : (
            <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100">
              {messages.slice(0, 50).map(m => (
                <div key={m.id} className="px-4 py-3 text-sm">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-xs text-slate-500">{(m.created_at || '').slice(0, 16).replace('T', ' ')}</span>
                    <span className="text-xs text-slate-500">{m.phone} · by {m.sent_by}</span>
                  </div>
                  <div className="text-slate-700 whitespace-pre-line">{m.body}</div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <WATemplateEditor open={!!editing} onClose={() => setEditing(null)} initial={editing} api={api} onSaved={load} />
    </div>
  );
}

function WATemplateEditor({ open, onClose, initial, api, onSaved }) {
  const [form, setForm] = useState({ trigger: 'manual', name: '', body: '', enabled: true });
  const [saving, setSaving] = useState(false);
  const isEdit = !!(initial && initial.id);
  useEffect(() => { if (open) setForm(initial || { trigger: 'manual', name: '', body: '', enabled: true }); }, [open, initial]);

  const save = async () => {
    if (!form.name || !form.body) { alert('Name and body required'); return; }
    setSaving(true);
    try {
      if (isEdit) await api('PATCH', `/admin/wa-templates/${initial.id}`, { name: form.name, body: form.body, enabled: form.enabled });
      else await api('POST', `/admin/wa-templates`, form);
      onSaved(); onClose();
    } catch (e) { alert(e.response?.data?.detail || e.message); } finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title={isEdit ? `Edit "${initial?.name}"` : 'New Template'}
      footer={<><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="tpl-save-btn">{saving ? <Loader2 size={14} className="animate-spin" /> : 'Save'}</Button></>}
    >
      <div className="space-y-3">
        <div><Label>Template Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="tpl-name" /></div>
        <div>
          <Label>Trigger</Label>
          <select className="h-10 w-full px-3 rounded-md border border-slate-300 bg-white text-sm" value={form.trigger} disabled={isEdit} onChange={(e) => setForm({ ...form, trigger: e.target.value })}>
            <option value="manual">Manual</option>
            <option value="booking_reminder">Booking Reminder (Day Before)</option>
            <option value="warranty_expiry">Warranty Expiry (7d before)</option>
            <option value="amc_renewal">AMC Renewal (30d before)</option>
            <option value="review_request">Review Request</option>
            <option value="payment_received">Payment Received</option>
          </select>
        </div>
        <div><Label>Body *</Label><Textarea rows={6} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="tpl-body" /></div>
        <div className="text-[11px] text-slate-500">Placeholders: <code>{'{{name}} {{date}} {{service}} {{phone}} {{amount}} {{link}}'}</code></div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
          Enabled
        </label>
      </div>
    </Modal>
  );
}

// ============================================================
// (g) REVIEWS
// ============================================================
export function ReviewsTab({ api }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [picking, setPicking] = useState(false);
  const [bookings, setBookings] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await api('GET', `/admin/reviews`); setRows(res.data || []); }
    catch (e) { /* */ } finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const filtered = filter === 'all' ? rows : rows.filter(r => r.status === filter);

  const moderate = async (r, status) => {
    try { await api('PATCH', `/admin/reviews/${r.id}`, { status }); await load(); }
    catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  const openPicker = async () => {
    try { const res = await api('GET', `/admin/bookings`); setBookings((res.data || []).filter(b => b.status === 'completed' || b.work_order?.status === 'completed')); setPicking(true); }
    catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  const request = async (b) => {
    try {
      const res = await api('POST', `/admin/reviews/request`, { booking_id: b.id });
      window.open(res.data.wa_url, '_blank');
      setPicking(false);
      await load();
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  return (
    <div data-testid="reviews-tab">
      <SectionHeader
        title="Reviews"
        subtitle="Post-service feedback · approve to display on the public site"
        actions={<Button onClick={openPicker} className="bg-emerald-600 hover:bg-emerald-700" data-testid="request-review-btn"><Send size={14} className="mr-1.5" /> Request Review</Button>}
      />

      <div className="flex gap-2 mb-3 flex-wrap">
        {['all', 'pending_response', 'received', 'approved', 'rejected'].map(s => (
          <button key={s}
            onClick={() => setFilter(s)}
            className={`px-3 py-1.5 text-xs font-semibold rounded-full ${filter === s ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
            data-testid={`review-filter-${s}`}
          >
            {s.replace('_', ' ')}
          </button>
        ))}
      </div>

      {loading && <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>}
      {!loading && filtered.length === 0 && <EmptyState icon={Star} title="No reviews in this filter" />}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {filtered.map(r => (
          <div key={r.id} className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`review-card-${r.id}`}>
            <div className="flex justify-between items-start mb-2">
              <div>
                <div className="font-semibold text-slate-900">{r.customer_name || '—'}</div>
                <div className="text-xs text-slate-500">{r.service_type || '—'} · {r.customer_phone || '—'}</div>
              </div>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                r.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                  r.status === 'received' ? 'bg-blue-100 text-blue-700' :
                    r.status === 'rejected' ? 'bg-red-100 text-red-700' :
                      'bg-amber-100 text-amber-700'
              }`}>{r.status.replace('_', ' ')}</span>
            </div>
            {r.rating && (
              <div className="flex gap-0.5 mb-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} size={14} className={i < r.rating ? 'fill-amber-400 text-amber-400' : 'text-slate-200'} />
                ))}
              </div>
            )}
            {r.title && <div className="font-semibold text-slate-800 text-sm">{r.title}</div>}
            {r.body && <div className="text-sm text-slate-600 mt-1">{r.body}</div>}
            {!r.body && r.status === 'pending_response' && (
              <div className="text-xs text-slate-400 italic">Awaiting customer response since {fmtDate(r.requested_at)}</div>
            )}
            {(r.status === 'received') && (
              <div className="mt-3 flex gap-2">
                <Button size="sm" onClick={() => moderate(r, 'approved')} className="bg-emerald-600 hover:bg-emerald-700"><CheckCircle2 size={13} className="mr-1" /> Approve</Button>
                <Button size="sm" variant="outline" onClick={() => moderate(r, 'rejected')} className="text-red-600"><X size={13} className="mr-1" /> Reject</Button>
              </div>
            )}
          </div>
        ))}
      </div>

      <Modal open={picking} onClose={() => setPicking(false)} title="Request review for…" wide>
        {bookings.length === 0 ? (
          <div className="text-sm text-slate-500">No completed bookings yet.</div>
        ) : (
          <div className="space-y-2">
            {bookings.slice(0, 30).map(b => (
              <div key={b.id} className="flex items-center justify-between p-3 border border-slate-200 rounded-lg">
                <div>
                  <div className="font-semibold">{b.customer_name}</div>
                  <div className="text-xs text-slate-500">{b.service_type || b.pest_type} · {fmtDate(b.scheduled_date)}</div>
                </div>
                <Button size="sm" onClick={() => request(b)}>Send WA</Button>
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
}
